<template>
  <div class="filter-container">
    <div v-for="(filter, index) in selectedFilters" :key="filter.fieldName" class="filter-field">
      <div
        class="plus"
        :class="index === selectedFilters.length - 1 ? 'highlighted' : ''"
        @click="addRemoveFilterField(index)"
      >
        <font-awesome-icon :icon="getPlusOrMinus(index)" />
      </div>
      <div v-if="index === selectedFilters.length - 1" class="more">{{ moreLessText }}</div>

      <div v-if="fieldIsSelected(index) && !fieldIsDateRange(filter)" class="filter-input">
        <div class="label">{{ filter.fieldName }}</div>

        <component
          :is="getFilterComponent(index).componentName"
          v-if="filter.fieldName === 'Containing Date'"
          v-model="containingDateVal"
          v-bind="getFilterComponent(index).properties"
          @change="onContainingDateChange(filter, $event)"
        />
        <component
          :is="getFilterComponent(index).componentName"
          v-else-if="filter.fieldName === 'End Date Up To'"
          v-model="endDateUpToVal"
          v-bind="getFilterComponent(index).properties"
          @change="onEndDateUpToChange(filter, $event)"
        />

        <component
          :is="getFilterComponent(index).componentName"
          v-else-if="booleanFields.includes(filter.fieldName)"
          v-bind="getFilterComponent(index).properties"
          @slide="onToggleBooleanValue(filter, $event)"
        />
        <component
          :is="getFilterComponent(index).componentName"
          v-else
          v-bind="getFilterComponent(index).properties"
          @input="onInput(filter, $event)"
          @item-change="onInput(filter, $event)"
        />
      </div>
      <!-- 
        TODO
        Listen to key events on the date input field
        so we can pproperly register when user deletes a previously selected value
        with backspace
      -->
      <div v-else-if="fieldIsSelected(index) && fieldIsDateRange(filter)" class="filter-input">
        <div class="label">{{ filter.fieldName }}</div>
        <component
          :is="getFilterComponent(index).componentName"
          v-model="startRangeDate[index]"
          v-bind="getFilterComponent(index).properties"
          @change="onStartRange(filter, $event, index)"
        />
        <span>and</span>
        <component
          :is="getFilterComponent(index).componentName"
          v-model="endRangeDate[index]"
          v-bind="getFilterComponent(index).properties"
          @change="onEndRange(filter, $event, index)"
        />
      </div>
    </div>

    <dropdown-select
      v-if="showFilterFields"
      :menu-items="filterFields"
      menu-label="Select from dropdown"
      class="bordered"
      @select-item="displayFilterInput($event)"
    />
  </div>
</template>

<script>
import InputField from '@/app/components/InputField';
import DropdownSelect from '@/app/components/DropdownSelect';
import TolMultiSelect from '@/app/components/TolMultiSelect';
import FormDatePicker from '@/app/components/FormDatePicker';
import ToggleSwitch from '@/app/components/ToggleSwitch';
import { searchUtils } from '@/mixins/search-utils';
import {
  AdvancedSearchGetters,
  AdvancedSearchActions
} from '@/app/features/advanced-search/advanced-search.keys';

export default {
  components: {
    DropdownSelect,
    InputField,
    TolMultiSelect,
    FormDatePicker,
    ToggleSwitch
  },
  mixins: [searchUtils],
  props: {
    filters: {
      type: [String, Array],
      default: null
    },
    table: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      showFilterFields: false,
      filterFieldSelected: false,
      filterFieldCount: 1,
      startRangeDate: { 1: null, 2: null },
      endRangeDate: { 1: null, 2: null },
      containingDateVal: null,
      endDateUpToVal: null,
      booleanFields: ['IMAC', 'Payrollee', 'Has Attachment'],
      currentBooleanVal: false
    };
  },
  computed: {
    filterFields() {
      let result;
      switch (this.table) {
        case 'Assignments':
          result = this.$store.getters[AdvancedSearchGetters.FilterFields]
            .filter(item => item.tables.includes('assignments'))
            .map(key => key.label);
          break;
        case 'Time Sheets':
          result = this.$store.getters[AdvancedSearchGetters.FilterFields]
            .filter(item => item.tables.includes('time sheets'))
            .map(key => key.label);
          break;
        case 'Expense Reports':
          result = this.$store.getters[AdvancedSearchGetters.FilterFields]
            .filter(item => item.tables.includes('expenses'))
            .map(key => key.label);
          break;
        case 'Perm Placements':
          result = this.$store.getters[AdvancedSearchGetters.FilterFields]
            .filter(item => item.tables.includes('perm placements'))
            .map(key => key.label);
          break;
      }
      return result;
    },
    statuses() {
      const result = this.$store.getters[AdvancedSearchGetters.TableStatuses]
        .filter(item => item.tables.includes(this.table.toLowerCase()))
        .map(key => key.status);
      return result;
    },
    branches() {
      return this.$store.getters[AdvancedSearchGetters.BranchNames].map(item => item.name);
    },
    divisions() {
      return this.$store.getters[AdvancedSearchGetters.Divisions].map(
        item => item.abbr + ' ' + item.name
      );
    },
    departments() {
      return this.$store.getters[AdvancedSearchGetters.Departments].map(
        item => item.code + ' - ' + item.description
      );
    },
    rebates() {
      return this.$store.getters[AdvancedSearchGetters.Rebates].map(
        item => `${item.percent.toFixed(2)}% ${item.code}`
      );
    },
    entities() {
      return this.$store.getters[AdvancedSearchGetters.EntityList];
    },
    selectedFilters() {
      return this.$store.getters[AdvancedSearchGetters.AdvancedSearchParams].advancedSearchFilters;
    },
    moreLessText() {
      return this.showFilterFields ? 'Less...' : 'More...';
    }
  },
  watch: {
    filters: {
      immediate: true,
      handler(val) {
        if (val && val.length != 0) {
          this.showDefaultUI();
        }
      }
    }
  },
  methods: {
    displayFilterInput(evt) {
      const booleanFields = ['Has Attachment', 'Payrollee', 'IMAC'];
      this.$store.dispatch(AdvancedSearchActions.SetSearchFilterField, {
        fieldName: evt,
        value: booleanFields.includes(evt) ? false : ''
      });
      this.showFilterFields = false;
    },
    addRemoveFilterField(val) {
      this.showFilterFields = !this.showFilterFields;
      if (!this.isEmptyFilterField(this.selectedFilters[val])) {
        this.$store.dispatch(AdvancedSearchActions.ClearSearchFilterField, val);
        this.showFilterFields = false;
      }
    },
    getPlusOrMinus(val) {
      return this.fieldIsSelected(val) ||
        (val === this.selectedFilters.length - 1 && this.showFilterFields)
        ? 'minus'
        : 'plus';
    },
    fieldIsSelected(val) {
      const currentField = this.selectedFilters[val];
      return !this.isEmptyFilterField(currentField);
    },
    fieldIsDateRange(val) {
      return ['starts between', 'stops between', 'end date is between'].includes(
        val.fieldName.toLowerCase()
      );
    },
    onInput(filterField, evt) {
      this.$store.dispatch(AdvancedSearchActions.SetSearchFilterValue, {
        fieldName: filterField.fieldName,
        value: evt
      });
    },
    onStartRange(filterField, evt, idx) {
      this.$set(this.startRangeDate, idx, evt);
      let val;
      if (this.endRangeDate[idx]) {
        val = [evt].concat(this.endRangeDate[idx]);
        this.$store.dispatch(AdvancedSearchActions.SetSearchFilterValue, {
          fieldName: filterField.fieldName,
          value: val
        });
      }
    },
    onEndRange(filterField, evt, idx) {
      this.$set(this.endRangeDate, idx, evt);
      let val;
      if (this.startRangeDate[idx]) {
        val = [this.startRangeDate[idx]].concat(evt);
        this.$store.dispatch(AdvancedSearchActions.SetSearchFilterValue, {
          fieldName: filterField.fieldName,
          value: val
        });
      }
    },
    onContainingDateChange(filterField, evt) {
      this.containingDateVal = evt;
      this.$store.dispatch(AdvancedSearchActions.SetSearchFilterValue, {
        fieldName: filterField.fieldName,
        value: evt
      });
    },
    onEndDateUpToChange(filterField, evt) {
      this.endDateUpToVal = evt;
      this.$store.dispatch(AdvancedSearchActions.SetSearchFilterValue, {
        fieldName: filterField.fieldName,
        value: evt
      });
    },

    onToggleBooleanValue(filterField, evt) {
      this.currentBooleanVal = !this.currentBooleanVal;
      this.$store.dispatch(AdvancedSearchActions.SetSearchFilterValue, {
        fieldName: filterField.fieldName,
        value: evt.checked
      });
    },
    isEmptyFilterField(val) {
      return !val.fieldName && !val.value;
    },
    getFilterComponent(index) {
      const comp = this;
      const fields = this.$store.state.advancedSearchState.filterFields;
      let selectedFilterField;
      const selected = this.selectedFilters[index];
      for (const field of fields) {
        if (field.label === selected.fieldName) {
          selectedFilterField = field;
          break;
        }
      }
      let result;
      switch (selectedFilterField.label) {
        case 'Status':
          result = {
            componentName: selectedFilterField.component,
            properties: {
              itemList: comp.statuses,
              fieldName: 'status'
            }
          };
          break;
        case 'Branch':
          result = {
            componentName: selectedFilterField.component,
            properties: {
              itemList: comp.branches,
              fieldName: 'branch'
            }
          };
          break;
        case 'Division':
          result = {
            componentName: selectedFilterField.component,
            properties: {
              itemList: comp.divisions,
              fieldName: 'division'
            }
          };
          break;
        case 'Department':
          result = {
            componentName: selectedFilterField.component,
            properties: {
              itemList: comp.departments,
              fieldName: 'department',
              filterOn: item => item.split(' - ')[0]
            }
          };
          break;
        case 'Rebate Code':
          return {
            componentName: selectedFilterField.component,
            properties: {
              itemList: comp.rebates,
              fieldName: 'rebate'
            }
          };
        case 'Entity':
          result = {
            componentName: selectedFilterField.component,
            properties: {
              itemList: comp.entities,
              fieldName: 'entity'
            }
          };
          break;

        default:
          result = this.getTextFields(selectedFilterField, selected);
      }
      return result;
    },

    getTextFields(selectedFilterField, selected) {
      /**
       * Helper function to reduce complexity of getFilterComponent function
       * (The linter is annoying!!)
       */
      let result;
      switch (selectedFilterField.label) {
        case 'Approver First Name':
        case 'Approver Last Name':
        case 'Contractor First Name':
        case 'Contractor Last Name':
        case 'Occupation':
        case 'Recruiter/Sales Last Name':
        case 'Recruiter/Sales First Name':
        case 'Client Name':
        case 'Pay To Name':
        case 'End Client Name':
        case 'Line Of Business(IBM Only)':
          result = {
            componentName: selectedFilterField.component,
            properties: {
              value: selected.value
            }
          };
          break;
        default:
          result = this.getNumericFields(selectedFilterField, selected);
      }
      return result;
    },

    getNumericFields(selectedFilterField, selected) {
      /**
       * Helper function to reduce complexity of getFilterComponent function
       * (The linter is annoying!!)
       */
      let result;
      switch (selectedFilterField.label) {
        case 'Recruiter/Sales Rep Number':
        case 'Invoice Number':
        case 'Client Number':
        case 'Payroll Number':
        case 'Work Order Number':
        case 'Purchase Order Number':
        case 'Work Authorization':
          result = {
            componentName: selectedFilterField.component,
            properties: {
              value: selected.value
            }
          };
          break;
        default:
          result = this.getDateFields(selectedFilterField, selected);
      }
      return result;
    },

    getDateFields(selectedFilterField, selected) {
      /**
       * Helper function to reduce complexity of getFilterComponent function
       * (The linter is annoying!!)
       */
      let result;
      switch (selectedFilterField.label) {
        case 'Starts Between':
        case 'Stops Between':
        case 'End Date is Between':
        case 'Containing Date':
        case 'End Date Up To':
          result = {
            componentName: selectedFilterField.component,
            properties: {
              value: selected.value,
              type: 'bordered'
            }
          };
          break;
        default:
          result = this.getBooleanFields(selectedFilterField);
      }

      return result;
    },

    getBooleanFields(selectedFilterField) {
      let result;
      switch (selectedFilterField.label) {
        case 'Has Attachment':
        case 'Payrollee':
        case 'IMAC':
          result = {
            componentName: selectedFilterField.component,
            properties: {
              isChecked: this.currentBooleanVal
            }
          };
          break;
      }

      return result;
    },
    showDefaultUI() {
      for (let filter of this.filters) {
        this.displayFilterInput(filter.label);
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/app/styles/colors';
@import '@/app/styles/variables';

.filter-container {
  .filter-field {
    display: flex;
    margin-bottom: 8px;
    .plus {
      display: block;
      position: relative;
      width: 40px;
      height: 40px;
      border: 1px solid $row-border;
      margin-right: 5px;
      //background-color: $blue;
      //color: $white;
      svg {
        color: $row-border;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
      &:hover {
        cursor: pointer;
        border-color: $black;
      }

      &.highlighted {
        background-color: $green;
        color: $white;
        font-weight: bold;
        &:hover {
          background-color: $secondary-green;
        }
      }
    }
    .more {
      display: flex;
      align-items: center;
    }

    .filter-input {
      display: flex;
      flex-grow: 0;
      flex-basis: 100%;
      border: 1px solid $blue;
      align-items: center;
      .label {
        display: flex;
        flex-basis: 20%;
        padding: 0 5px;
        background-color: $blue;
        height: 100%;
        color: $white;
        align-items: center;
      }
      .dropdown-container,
      .input-container,
      .popover-container {
        display: flex;
        flex-grow: 1;
      }
      span {
        display: inline-block;
        padding: 0 5px;
      }
    }
  }
}
</style>
